import { Row, flexRender } from '@tanstack/react-table';
import React from 'react';

import { ReactiveRowProps } from '../table/rows/reactive-row.component';

export interface ReactiveRowElementProps<T extends object> extends ReactiveRowProps {
    className?: string;
    row: Row<T>;
    onClick?: (element: T) => void;
}

/**
 * Row renderer for reactive lists. Identical to the v1 `RowElement` apart from `isPendingDelete`,
 * which marks a row whose entity was just deleted for the short window before it is removed.
 */
export const ReactiveRowElement = <T extends object>(props: ReactiveRowElementProps<T>) => {
    const isDeleted = props.isPendingDelete || (props.row.original as { delete_timestamp?: string | null })?.delete_timestamp;

    return (
        <tr
            data-testid={`row-${props.row.index}`}
            data-pending-delete={props.isPendingDelete ? 'true' : undefined}
            id={props.row.id}
            key={props.row.id}
            onClick={() => props.onClick && props.onClick(props.row.original)}
            className={
                'border-border-default border-b transition-opacity duration-500 ' +
                (props.row.getIsExpanded() && props.row.getCanExpand() ? `bg-gray-${500 - props.row.depth * 100}` : ' ') +
                (props.className || (props.onClick ? 'hover:bg-background-default hover:cursor-pointer' : '')) +
                (isDeleted ? ' bg-status-danger/50' : '') +
                (props.isPendingDelete ? ' pointer-events-none opacity-50' : '')
            }>
            {props.row.getVisibleCells().map((cell) => flexRender(cell.column.columnDef.cell, cell.getContext()))}
        </tr>
    );
};
